// 活动系统类型定义

// 活动状态
export enum ActivityStatus {
	DRAFT = "draft", // 草稿，未配置完成
	PENDING = "pending", // 待开始
	ACTIVE = "active", // 进行中
	ENDED = "ended", // 已结束
	CANCELLED = "cancelled", // 已取消
}

// 活动类别键值
export enum ActivityCategoryKey {
	LIQUIDITY_AIRDROP = "LIQUIDITY_AIRDROP", // 流动池空投
	INVITE_REWARD = "INVITE_REWARD", // 邀请返利
	LOTTERY_WHEEL = "LOTTERY_WHEEL", // 转盘抽奖
}

// 奖励类型
export enum RewardType {
	TOKEN = "token",
	NFT = "nft",
	POINTS = "points",
	VOUCHER = "voucher",
	OTHER = "other",
}

// 奖励状态
export enum RewardStatus {
	PENDING = "pending",
	PROCESSING = "processing",
	SENT = "sent",
	CLAIMED = "claimed",
	FAILED = "failed",
	EXPIRED = "expired",
}

// 参与状态
export enum ParticipantStatus {
	JOINED = "joined",
	COMPLETED = "completed",
	FAILED = "failed",
	CANCELLED = "cancelled",
}

// 多语言文本
export interface I18nText {
	en: string;
	zh: string;
}

// 活动类别定义
export interface ActivityCategory {
	id: number;
	key: string;
	name: I18nText;
	description: I18nText;
	icon?: string;
	minVipLevel: number;
	configSchema: any; // JSON Schema
	displayFields?: {
		primary?: string;
		secondary?: string;
		badge?: string;
	};
	isActive: boolean;
	sortOrder: number;
	createdAt: Date;
	updatedAt: Date;
}

// 流动池空投配置
export interface LPAirdropConfig {
	tokenId: string;
	airdropTokenId: string;
	intervalDays: number;
	totalAmountPerRound: number;
	distributionMethod: "proportional" | "fixed";
	minLpThreshold: number;
	weightTiers: WeightTier[];
}

// 权重层级
export interface WeightTier {
	minRank: number;
	maxRank: number;
	weight: number;
}

// 活动主体
export interface ActivityCampaign {
	id: string;
	creatorPrincipal: string;
	name: string;
	description?: string;
	imageUrl?: string;
	categoryKey: string;
	status: ActivityStatus;
	startTime: Date;
	endTime: Date;
	minVipLevel: number;
	maxParticipants?: number;
	currentParticipants: number;
	configData: any; // 活动配置数据
	statistics?: any; // 统计数据
	tags?: string[];
	isFeatured: boolean;
	createdAt: Date;
	updatedAt: Date;

	// 关联数据（可选）
	category?: ActivityCategory;
	isCreator?: boolean;
	isParticipant?: boolean;
	participantInfo?: ActivityParticipant;

	// 类型特定的配置（可选）
	lpAirdropConfig?: LPAirdropConfig;
}

// 活动参与者
export interface ActivityParticipant {
	id: string;
	campaignId: string;
	userPrincipal: string;
	joinedAt: Date;
	status: ParticipantStatus;
	contributionData?: any;
	resultData?: any;
	updatedAt: Date;
}

// 活动奖励
export interface ActivityReward {
	id: string;
	campaignId: string;
	participantId?: string;
	userPrincipal: string;
	rewardType: RewardType;
	rewardAmount?: number;
	rewardDetails?: any;
	status: RewardStatus;
	transactionHash?: string;
	issuedAt: Date;
	claimedAt?: Date;
	expiresAt?: Date;
	notes?: string;
}

// 活动日志
export interface ActivityLog {
	id: string;
	campaignId: string;
	operatorPrincipal: string;
	action:
		| "create"
		| "update"
		| "start"
		| "pause"
		| "resume"
		| "end"
		| "cancel"
		| "config";
	details?: any;
	ipAddress?: string;
	userAgent?: string;
	createdAt: Date;
}

// 活动模板
export interface ActivityTemplate {
	id: string;
	name: string;
	description?: string;
	categoryKey: string;
	configTemplate: any;
	isPublic: boolean;
	creatorPrincipal?: string;
	usageCount: number;
	createdAt: Date;
	updatedAt: Date;
}

// 创建活动请求
export interface CreateActivityRequest {
	name: string;
	description?: string;
	imageBase64?: string; // Base64编码的图片，限制200KB
	categoryKey: string;
	startTime: string;
	endTime: string;
	minVipLevel?: number;
	maxParticipants?: number;
	tags?: string[];
	configData?: any;
}

// 更新活动请求
export interface UpdateActivityRequest {
	name?: string;
	description?: string;
	imageBase64?: string;
	startTime?: string;
	endTime?: string;
	minVipLevel?: number;
	maxParticipants?: number;
	tags?: string[];
	configData?: any;
	status?: ActivityStatus;
}

// 活动列表查询参数
export interface ActivityListQuery {
	status?: ActivityStatus | ActivityStatus[];
	categoryKey?: string;
	creatorPrincipal?: string;
	participantPrincipal?: string;
	isFeatured?: boolean;
	startTimeFrom?: string;
	startTimeTo?: string;
	endTimeFrom?: string;
	endTimeTo?: string;
	search?: string;
	tags?: string[];
	page?: number;
	pageSize?: number;
	sortBy?: "createdAt" | "startTime" | "endTime" | "participants";
	sortOrder?: "asc" | "desc";
}

// 活动统计
export interface ActivityStatistics {
	totalParticipants: number;
	totalRewards: number;
	claimedRewards: number;
	successRate?: number;
	averageReward?: number;
	topParticipants?: Array<{
		principal: string;
		contribution: number;
		reward?: number;
	}>;
	dailyStats?: Array<{
		date: string;
		participants: number;
		rewards: number;
	}>;
}

// 活动配置数据类型

// 流动池空投配置
export interface LiquidityAirdropConfig {
	tokenId: string;
	totalAmount: number;
	minLiquidity: number;
	distributionType: "proportional" | "equal" | "tiered";
	snapshotTime?: string;
	tiers?: Array<{
		minAmount: number;
		maxAmount: number;
		rewardMultiplier: number;
	}>;
}

// 邀请返利配置
export interface InviteRewardConfig {
	rewardPerInvite: number;
	maxInvites: number;
	minInviteeAction: "register" | "trade" | "deposit";
	rewardTokenId: string;
	tierRewards?: Array<{
		invites: number;
		bonus: number;
	}>;
	requireVerification?: boolean;
}

// 转盘抽奖配置
export interface LotteryWheelConfig {
	entryCost: number;
	entryTokenId: string;
	prizes: Array<{
		id?: string;
		name: string;
		type: "token" | "nft" | "points";
		amount: number;
		tokenId?: string;
		probability: number; // 0-100
		quantity: number;
		image?: string;
	}>;
	maxSpinsPerUser: number;
	dailyLimit?: number;
	guaranteedPrize?: boolean;
}

// API响应类型
export interface ActivityApiResponse<T = any> {
	success: boolean;
	data?: T;
	error?: string;
	message?: string;
}

// 分页响应
export interface PaginatedResponse<T> {
	items: T[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

// 活动操作权限
export interface ActivityPermissions {
	canCreate: boolean;
	canEdit: boolean;
	canDelete: boolean;
	canStart: boolean;
	canPause: boolean;
	canEnd: boolean;
	canViewParticipants: boolean;
	canManageRewards: boolean;
}

// 活动卡片显示数据
export interface ActivityCardDisplay {
	id: string;
	name: string;
	description?: string;
	imageUrl?: string;
	category: {
		key: string;
		name: I18nText;
		icon?: string;
	};
	status: ActivityStatus;
	statusText: string;
	statusColor: string;
	creator: {
		principal: string;
		displayName?: string;
		isVip?: boolean;
	};
	timeInfo: {
		startTime: Date;
		endTime: Date;
		timeRemaining?: string;
		progress?: number;
	};
	stats: {
		participants: number;
		maxParticipants?: number;
		primaryValue?: string | number;
		primaryLabel?: string;
		secondaryValue?: string | number;
		secondaryLabel?: string;
		badge?: string;
	};
	actions: {
		canJoin: boolean;
		canView: boolean;
		canEdit: boolean;
		canShare: boolean;
	};
	tags?: string[];
	isFeatured: boolean;
}
